
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { extractProgramName, extractProgramOverview } from "@/utils/program-data-parser";

export function useProgramData(programId: string | undefined, navigate: (path: string) => void) {
  const [programName, setProgramName] = useState<string>("Loading program...");
  const [programOverview, setProgramOverview] = useState<string | null>(null);
  const [programData, setProgramData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProgram = async () => {
      if (!programId) {
        console.error("No program ID provided");
        navigate("/programs");
        return;
      }
      
      console.log(`Fetching data for program ID: ${programId}`);
      
      try {
        setLoading(true);
        setError(null);
        
        // Check the current user to help debug RLS issues
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError) {
          console.error("Error getting current user:", userError);
        } else {
          console.log("Current authenticated user:", userData.user?.id);
        }
        
        // First try to fetch from program_data table specifically
        const { data: programDataResult, error: programDataError } = await supabase
          .from('program_data')
          .select('data')
          .eq('program_id', programId)
          .maybeSingle();
          
        if (programDataError) {
          console.error("Error fetching from program_data table:", programDataError);
          // Don't throw here, we'll try the programs table next
        } else {
          console.log("Response from program_data table:", programDataResult);
        }
          
        if (programDataResult && programDataResult.data) {
          console.log("Found data in program_data table:", programDataResult.data);
          
          // Check if data is empty object
          if (typeof programDataResult.data === 'object' && 
              Object.keys(programDataResult.data).length === 0) {
            console.log("program_data exists but contains empty object");
          }
          
          const overview = extractProgramOverview(programDataResult.data);
          if (overview) {
            setProgramOverview(overview);
            console.log("Extracted overview from program_data:", overview);
          }
        } else {
          console.log("No data found in program_data table or data is null");
        }
          
        // Also fetch from programs table
        const { data, error } = await supabase
          .from('programs')
          .select('name, program_data, ai_context')
          .eq('program_id', programId)
          .maybeSingle();
          
        if (error) {
          console.error("Error fetching from programs table:", error);
          throw error;
        } else {
          console.log("Response from programs table:", data);
        }
        
        // Check if program exists and set program data
        if (data) {
          console.log("Raw program data from programs table:", data);
          
          // Set program data for use in rendering
          setProgramData(data.program_data);
          
          // Get program name from different possible sources
          const extractedName = extractProgramName(
            data.program_data,
            data.ai_context,
            data.name
          );
              
          setProgramName(extractedName);
          
          // Extract overview from program data if not already found
          if (!programOverview && data.program_data && typeof data.program_data === 'object') {
            const overview = extractProgramOverview(data.program_data);
            console.log("Program overview extracted from programs table:", overview);
            
            // Set the overview
            if (overview) {
              setProgramOverview(overview);
            } else {
              console.log("No overview could be extracted from program_data");
            }
          }
        } else {
          // Program not found
          setError("Program not found");
          console.error("Program not found in database with ID:", programId);
          
          toast({
            title: "Program not found",
            description: "The program you're looking for doesn't exist or you don't have permission to view it.",
            variant: "destructive"
          });
          
          // Add a small delay before navigation to allow the toast to be seen
          setTimeout(() => navigate("/programs"), 2000);
        }
      } catch (error) {
        console.error("Error fetching program:", error);
        setError("Error loading program");
        
        toast({
          title: "Error loading program",
          description: "There was an error loading the program. Please try again.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchProgram();
  }, [programId, navigate, programOverview]);

  return { programName, programOverview, programData, loading, error };
}
